import { PlusIcon } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useState } from "react";

import { getDonorIds } from "@blis/shared/models/subject";

import { DetailItem } from "@/components/DetailItem";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/toast";
import { SubjectCreateDialog } from "@/features/subjects/components/SubjectCreateDialog";
import type { TSubjectDetail } from "@/lib/types";
import { useAccessionForm } from "./AccessionFormProvider";
import { SubjectSelect } from "./components/SubjectSelect";
import { FormSection } from "./layout";

export function DonorSection() {
  const { form, setAccessions } = useAccessionForm();
  const [subject, setSubject] = useState<TSubjectDetail | undefined>();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [startingValue, setStartingValue] = useState<{
    value: string;
    type: "caseId" | "unosId";
  } | null>(null);

  const onSubjectSelected = (subject: TSubjectDetail) => {
    setDialogOpen(false);
    setSubject(subject);
    form.setFieldValue("subjectId", subject.id);
    setAccessions(subject.id);
    form.validateField("subjectId", "change");
  };

  return (
    <form.Field name="subjectId">
      {({ state }) => (
        <FormSection
          title="Donor"
          description="Select an existing donor or create a new one."
          error={state.meta.errors.join(", ")}
          actions={
            <div className="flex items-center gap-2">
              <SubjectSelect
                value={subject}
                onValueChanged={onSubjectSelected}
                onCreateSelected={(val) => {
                  setStartingValue(val);
                  setDialogOpen(true);
                }}
              />
              <SubjectCreateDialog
                open={dialogOpen}
                onOpenChange={setDialogOpen}
                startingValue={startingValue}
                onAlreadyExists={(subject) => {
                  toast.success(
                    "Donor already exists, selected existing donor",
                  );
                  onSubjectSelected(subject);
                }}
                onCreated={onSubjectSelected}
              >
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onPress={() => setDialogOpen(true)}
                >
                  <PlusIcon />
                </Button>
              </SubjectCreateDialog>
            </div>
          }
        >
          <AnimatePresence>
            {subject && (
              <motion.div
                key={subject.id}
                initial={{ height: 0 }}
                animate={{ height: "auto" }}
                exit={{ height: 0 }}
                className="overflow-hidden bg-background rounded-b-md"
              >
                <SubjectCard subject={subject} />
                {/* <input
              type="hidden"
              {...register("subjectId", { valueAsNumber: true })}
              value={subject.id}
            /> */}
              </motion.div>
            )}
          </AnimatePresence>
        </FormSection>
      )}
    </form.Field>
  );
}

function SubjectCard({ subject }: { subject: TSubjectDetail }) {
  const ids = getDonorIds(subject);

  return (
    <div className="p-4 grid grid-cols-2 md:grid-cols-3 gap-2">
      {!subject.type ||
        (subject.type === "donor" && (
          <>
            <DetailItem title="Donor ID">{subject.caseId}</DetailItem>
            <DetailItem title="UNOS ID">{ids.unosId ?? "-"}</DetailItem>
            <DetailItem title="Alternate IDs">
              {ids.donorIds.length ? ids.donorIds.join(", ") : "-"}
            </DetailItem>
          </>
        ))}
      {subject.type === "product" && (
        <DetailItem title="Product ID">{subject.caseId}</DetailItem>
      )}
    </div>
  );
}
